import { useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import AppDialog from "~/shared/ui/AppDialog";
import { mergePackages, previewMerge } from "~/services/antecipebackoffice/packageMoves";
import { getApiErrorCode, getApiErrorMessage, getApiReasons, getMergedIntoId } from "~/utils/apiError";
import { brl, formatIsoDateBR, joinIdsPt, plural } from "~/utils/format";
import { STATUS_SHORT_LABEL } from "~/utils/mergeSelection";
import { reasonText } from "./mergeReasons";

export default function JoinPackagesDialog({ open, rows, onCancel, onMerged }) {
  const sortedRows = useMemo(() => [...rows].sort((a, b) => a.id - b.id), [rows]);
  const ids = useMemo(() => sortedRows.map((r) => r.id), [sortedRows]);
  const [preview, setPreview] = useState({ loading: true, error: null, data: null, reasons: [] });
  const [attempt, setAttempt] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitReasons, setSubmitReasons] = useState([]);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setPreview({ loading: true, error: null, data: null, reasons: [] });
    previewMerge(ids)
      .then((data) => {
        if (alive) setPreview({ loading: false, error: null, data, reasons: data.ok ? [] : data.reasons || [] });
      })
      .catch((e) => {
        if (!alive) return;
        if (getApiErrorCode(e) === "FEATURE_DISABLED") {
          setPreview({ loading: false, error: null, data: null, reasons: [{ code: "FEATURE_DISABLED" }] });
          return;
        }
        setPreview({ loading: false, error: getApiErrorMessage(e, "Não foi possível montar a prévia."), data: null, reasons: getApiReasons(e) });
      });
    return () => {
      alive = false;
    };
  }, [open, ids, attempt]);

  const reasons = submitReasons.length ? submitReasons : preview.reasons;
  const previewOk = Boolean(preview.data && preview.data.ok);
  const result = previewOk ? preview.data.result : null;

  const confirm = async () => {
    if (!previewOk || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const data = await mergePackages(ids);
      onMerged({ id: data.advancePayment.id, mergedFrom: data.mergedFrom || ids, movedVacancies: data.movedVacancies ?? null, alreadyMerged: false });
    } catch (e) {
      const code = getApiErrorCode(e);
      const mergedInto = getMergedIntoId(e);
      const apiReasons = getApiReasons(e);
      // Ruling P3: só é "já estavam agrupados" quando TODOS os motivos são ALREADY_MERGED;
      // o AB manda ALREADY_MERGED primeiro mesmo quando só uma origem foi agrupada.
      const onlyAlreadyMerged = apiReasons.length
        ? apiReasons.every((reason) => reason.code === "ALREADY_MERGED")
        : code === "ALREADY_MERGED";
      if (code === "ALREADY_MERGED" && onlyAlreadyMerged && mergedInto) {
        onMerged({ id: mergedInto, mergedFrom: ids, movedVacancies: null, alreadyMerged: true });
        return;
      }
      if (code === "FEATURE_DISABLED") {
        setSubmitReasons([{ code }]);
        return;
      }
      if (apiReasons.length) setSubmitReasons(apiReasons);
      else setSubmitError(getApiErrorMessage(e, "Não foi possível juntar os pacotes. Nada foi alterado."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={onCancel}
      busy={submitting}
      size="md"
      title={`Juntar ${rows.length} pacotes`}
      actions={
        <>
          <Button onClick={onCancel} disabled={submitting} sx={{ color: "text.primary" }}>
            Cancelar
          </Button>
          <LoadingButton variant="contained" loading={submitting} onClick={confirm} disabled={!previewOk || submitReasons.length > 0}>
            Juntar em 1 pacote
          </LoadingButton>
        </>
      }
    >
      {preview.loading && (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <CircularProgress size={20} /> Montando a prévia…
        </Box>
      )}
      {preview.error && (
        <Alert severity="error" sx={{ mb: 1 }} action={<Button color="inherit" size="small" onClick={() => setAttempt((a) => a + 1)}>Tentar de novo</Button>}>
          {preview.error}
        </Alert>
      )}
      {reasons.length > 0 && (
        <Alert severity="error" sx={{ mb: 1 }}>
          <b>Não dá para juntar estes pacotes:</b>
          <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
            {reasons.map((reason, index) => (
              <li key={`${reason.code}-${reason.advancePaymentId || index}`}>{reasonText(reason)}</li>
            ))}
          </ul>
        </Alert>
      )}
      {result && (
        <>
          <Typography sx={{ fontSize: 16, mb: 1 }}>
            Vão virar <b>1 pacote novo com {plural(result.vacanciesQty, "plantão", "plantões")}</b>, <b>{brl(result.grossTotal)}</b> em valor bruto.
          </Typography>
          <Typography sx={{ mb: 1 }}>Reúne {joinIdsPt(ids)}.</Typography>
          <Box component="dl" sx={{ display: "grid", gridTemplateColumns: "max-content 1fr", columnGap: 2, rowGap: 0.5, m: 0, mb: 1 }}>
            <dt>Médico</dt><dd style={{ margin: 0 }}>{result.doctor?.name} · CRM {result.doctor?.crm}/{result.doctor?.crmUf}</dd>
            <dt>Empresa</dt><dd style={{ margin: 0 }}>{result.company?.name}</dd>
            <dt>Status</dt><dd style={{ margin: 0 }}>{STATUS_SHORT_LABEL[result.status] || result.status} (igual em todos)</dd>
            <dt>Pagamento do pacote</dt>
            <dd style={{ margin: 0 }}>
              {result.paymentDate ? formatIsoDateBR(result.paymentDate) : "a definir"} (igual em todos; a data dos plantões pode variar; nada é recalculado)
            </dd>
          </Box>
          <Box data-testid="join-table-scroll" sx={{ overflowX: "auto", mb: 1 }}>
            <Box component="table" sx={{ width: "100%", minWidth: 420, fontSize: 13, borderCollapse: "collapse" }}>
              <thead><tr><th align="left">Pacote</th><th align="right">Plantões</th><th align="right">Valor bruto</th></tr></thead>
              <tbody>
                {sortedRows.map((row) => (
                  <tr key={row.id}>
                    <td>#{row.id} {row.originAdvancePaymentId && <Chip size="small" color="warning" variant="outlined" label="filho de split" />}</td>
                    <td align="right">{row.vacanciesQty}</td>
                    <td align="right">{brl(row.grossTotal)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr><td><b>Pacote novo</b></td><td align="right"><b>{result.vacanciesQty}</b></td><td align="right"><b>{brl(result.grossTotal)}</b></td></tr></tfoot>
            </Box>
          </Box>
          <Alert severity="info">
            Os pacotes de origem ficam como <b>“Agrupado no #novo”</b>: saem da lista de trabalho mas continuam consultáveis. <b>Não dá para desfazer.</b> No
            app, o médico passa a ver um pedido só.
          </Alert>
        </>
      )}
      {submitError && <Alert severity="error" sx={{ mt: 1 }}>{submitError}</Alert>}
    </AppDialog>
  );
}
